import React, { useEffect, useState } from 'react';
import BackButton from '../../../../BackButton';
import OptionEditTable from './OptionEditTable';
import axiosInstance from '../../../../../interceptor/axiosInstance';
import { useLocation } from 'react-router-dom';
import AddNewOptions from './AddNewOptions';
import './OptionsTable.css';

// Toast notification function
const toast = {
  success: (message) => {
    const toast = document.createElement('div');
    toast.className = 'custom-toast custom-toast-success';
    toast.textContent = message;
    document.body.appendChild(toast);
    setTimeout(() => toast.classList.add('show'), 10);
    setTimeout(() => {
      toast.classList.remove('show');
      setTimeout(() => document.body.removeChild(toast), 300);
    }, 3000);
  },
  error: (message) => {
    const toast = document.createElement('div');
    toast.className = 'custom-toast custom-toast-error';
    toast.textContent = message;
    document.body.appendChild(toast);
    setTimeout(() => toast.classList.add('show'), 10);
    setTimeout(() => {
      toast.classList.remove('show');
      setTimeout(() => document.body.removeChild(toast), 300);
    }, 3000);
  },
};

// Skeleton Loader Component
const SkeletonRow = () => (
  <tr className="options-skeleton-row">
    <td className="options-td"><div className="options-skeleton options-skeleton-text"></div></td>
    <td className="options-td"><div className="options-skeleton options-skeleton-badge"></div></td>
    <td className="options-td"><div className="options-skeleton options-skeleton-image"></div></td>
    <td className="options-td">
      <div className="options-skeleton-actions">
        <div className="options-skeleton options-skeleton-btn"></div>
        <div className="options-skeleton options-skeleton-btn"></div>
      </div>
    </td>
  </tr>
);

// Empty State Component
const EmptyState = () => (
  <tr>
    <td colSpan="4" className="options-empty-cell">
      <div className="options-empty-state">
        <div className="options-empty-icon">📝</div>
        <h3 className="options-empty-title">No Options Found</h3>
        <p className="options-empty-message">
          This question doesn't have any options yet.<br />
          Click "Add New Option" to create one.
        </p>
      </div>
    </td>
  </tr>
);

const OptionsTable = () => {
  const location = useLocation();
  const questionID = location.state?.questionID;

  const [optionsData, setOptionsData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showPopup, setShowPopup] = useState(false);
  const [selectedOptionId, setSelectedOptionId] = useState(null);
  const [showAddPopup, setShowAddPopup] = useState(false);
  const [deletingOptionId, setDeletingOptionId] = useState(null);
  const [editingOptionId, setEditingOptionId] = useState(null); // ✅ track edit loader

  const fetchOptions = async () => {
    setLoading(true);
    try {
      const response = await axiosInstance.get(`assessments/options/?question_id=${questionID}`);
      setOptionsData(response.data.data);
    } catch (error) {
      console.error('Error fetching options:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOptions();
  }, [questionID]);

  const openEditPopup = (optionId) => {
    setEditingOptionId(optionId); // Start loader
    setSelectedOptionId(optionId);
    setShowPopup(true);
  };

  const closeEditPopup = () => {
    setEditingOptionId(null); // Reset loader
    setSelectedOptionId(null);
    setShowPopup(false);
  };

  const handleDeleteOption = async (optionId) => {
    const confirmDelete = window.confirm('Are you sure you want to delete this option?');
    
    if (confirmDelete) {
      setDeletingOptionId(optionId);
      try {
        await axiosInstance.delete(`assessments/options/${optionId}/`);
        toast.success('Option deleted successfully!');
        fetchOptions();
      } catch (error) {
        toast.error('Failed to delete option. Please try again.');
        console.error('Error deleting option:', error);
      } finally {
        setDeletingOptionId(null);
      }
    }
  };

  return (
    <div className="options-table-container">
      {/* Header */}
      <div className="options-table-header">
        <div className="options-table-header-left">
          <BackButton />
          <h1 className="options-table-title">Options Management</h1>
          {!loading && (
            <span className="options-count-badge">
              {optionsData.length} {optionsData.length === 1 ? 'Option' : 'Options'}
            </span>
          )}
        </div>
        <div className="options-table-header-right">
          <button
            className="options-add-btn"
            onClick={() => setShowAddPopup(true)}
          >
            <span className="options-add-icon">+</span>
            Add New Option
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="options-table-wrapper">
        <table className="options-table">
          <thead>
            <tr>
              <th className="options-th options-th-text">Option Text</th>
              <th className="options-th options-th-correct">Correct</th>
              <th className="options-th options-th-image">Image</th>
              <th className="options-th options-th-actions">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              // Skeleton loaders
              Array.from({ length: 5 }).map((_, idx) => <SkeletonRow key={idx} />)
            ) : optionsData.length === 0 ? (
              // Empty state
              <EmptyState />
            ) : (
              // Actual data
              optionsData.map((option) => (
                <tr key={option.id} className="options-table-row">
                  <td className="options-td options-td-text">
                    <span className="options-text-content">{option.text}</span>
                  </td>
                  <td className="options-td">
                    <span className={`options-correct-badge ${option.is_correct ? 'options-correct-yes' : 'options-correct-no'}`}>
                      {option.is_correct ? '✓ Correct' : '✗ Incorrect'}
                    </span>
                  </td>
                  <td className="options-td">
                    {option.image ? (
                      <img
                        src={`${axiosInstance.defaults.baseURL}${option.image}`}
                        alt="Option"
                        className="options-image-preview"
                      />
                    ) : (
                      <span className="options-no-image">No Image</span>
                    )}
                  </td>
                  <td className="options-td options-td-actions">
                    <div className="options-action-buttons-wrapper">
                      <button
                        className="options-action-btn options-edit-btn"
                        onClick={() => openEditPopup(option.id)}
                        disabled={editingOptionId === option.id}
                        title="Edit Option"
                      >
                        {editingOptionId === option.id ? (
                          <>
                            <span className="options-btn-spinner"></span>
                            Opening...
                          </>
                        ) : (
                          <>✏️ Edit</>
                        )}
                      </button>
                      <button
                        className="options-action-btn options-delete-btn"
                        onClick={() => handleDeleteOption(option.id)}
                        disabled={deletingOptionId === option.id}
                        title="Delete Option"
                      >
                        {deletingOptionId === option.id ? (
                          <>
                            <span className="options-btn-spinner"></span>
                            Deleting...
                          </>
                        ) : (
                          <>🗑️ Delete</>
                        )}
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Edit Modal */}
      {showPopup && selectedOptionId && (
        <OptionEditTable
          optionId={selectedOptionId}
          onClose={closeEditPopup}
          onSaveSuccess={fetchOptions}
        />
      )}

      {/* Add Modal */}
      {showAddPopup && (
        <AddNewOptions
          questionID={questionID}
          onClose={() => setShowAddPopup(false)}
          onSaveSuccess={fetchOptions}
        />
      )}
    </div>
  );
};

export default OptionsTable;
